import { FC, ReactElement } from "react";
import { Box, Chip, Tooltip, styled } from "@mui/material";

// --- Utility Function ---
export const getMismatchTooltipText = (
  isExporter?: boolean,
  isManufacturer?: boolean
): string => {
  if (isExporter && isManufacturer) {
    return "High confidence: Mismatches found in both Origin vs. Export and Origin vs. Manufacturer.";
  }
  if (isExporter) {
    return "Medium-high confidence: Mismatch found in Origin vs. Export.";
  }
  if (isManufacturer) {
    return "Medium confidence: Mismatch found in Origin vs. Manufacturer.";
  }
  return "Low likelihood: No mismatches detected.";
};

interface StatusChipProps {
  statusColor?: "success" | "warning" | "error" | "info" | "default";
}

const StyledStatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "statusColor",
})<StatusChipProps>(({ statusColor }) => {
  const baseStyles = {
    height: "24px",
    borderRadius: "6px",
    fontWeight: 600,
    border: "1px solid",
    cursor: "pointer",
    "&:hover": {
      opacity: 0.9,
    },
  };

  switch (statusColor) {
    case "success":
      return {
        ...baseStyles,
        color: "#1B5E20",
        backgroundColor: "#E8F5E9",
        borderColor: "#C8E6C9",
      };
    case "warning":
      return {
        ...baseStyles,
        color: "#E65100",
        backgroundColor: "#FFF3E0",
        borderColor: "#FFE0B2",
      };
    case "error":
      return {
        ...baseStyles,
        color: "#B71C1C",
        backgroundColor: "#FFEBEE",
        borderColor: "#FFCDD2",
      };
    case "info":
      return {
        ...baseStyles,
        color: "#0D47A1",
        backgroundColor: "#E3F2FD",
        borderColor: "#BBDEFB",
      };
    default:
      return {
        ...baseStyles,
        color: "#424242",
        backgroundColor: "#F5F5F5",
        borderColor: "#E0E0E0",
      };
  }
});

export const getStatusColor = (
  status: string | undefined
): "success" | "warning" | "error" | "info" | "default" => {
  switch (status) {
    case "Not contacted":
      return "default";
    case "Contacted":
      return "info";
    case "Vendor response received":
      return "warning";
    case "Validate and report drafting":
      return "success";
    case "Not eligible for First Sale":
      return "error";
    case "Engage customs broker":
      return "success";
    default:
      return "default";
  }
};

interface StatusDisplayProps {
  status: string | undefined;
  hideTooltip?: boolean;
  onClick?: () => void;
}

export const StatusDisplay: FC<StatusDisplayProps> = ({
  status,
  hideTooltip,
  onClick,
}): ReactElement => {
  const statusColor = getStatusColor(status);
  const displayStatus = status || "Not contacted";

  return (
    <Tooltip
      title={hideTooltip ? "" : "Click to view progress details"}
      placement="top"
    >
      <Box onClick={onClick} sx={{ cursor: onClick ? "pointer" : "default" }}>
        <StyledStatusChip
          label={displayStatus}
          statusColor={statusColor}
          size="small"
        />
      </Box>
    </Tooltip>
  );
};

// --- Mismatch Display Component ---
interface MismatchChipProps {
  customcolor?: "yellow" | "green" | "red";
}

const StyledChip = styled(Chip)<MismatchChipProps>(({ theme, customcolor }) => {
  const baseStyles = {
    height: "24px",
    borderRadius: "6px",
    fontWeight: 500,
    border: "1px solid",
  };

  switch (customcolor) {
    case "yellow":
      return {
        ...baseStyles,
        color: "#92400E",
        backgroundColor: "#FEF3C7",
        borderColor: "#FDE68A",
      };
    case "green":
      return {
        ...baseStyles,
        color: "#065F46",
        backgroundColor: "#D1FAE5",
        borderColor: "#A7F3D0",
      };
    case "red":
      return {
        ...baseStyles,
        color: "#991B1B",
        backgroundColor: "#FEE2E2",
        borderColor: "#FECACA",
      };
    default:
      return {
        ...baseStyles,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.grey[100],
        borderColor: theme.palette.grey[300],
      };
  }
});

interface MismatchDisplayProps {
  isExporter?: boolean;
  isManufacturer?: boolean;
}

export const MismatchDisplay: FC<MismatchDisplayProps> = ({
  isExporter,
  isManufacturer,
}): ReactElement => {
  const tooltipText = getMismatchTooltipText(isExporter, isManufacturer);

  return (
    <Tooltip title={tooltipText} placement="top">
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
        }}
      >
        {isExporter && isManufacturer ? (
          <>
            <StyledChip
              label="Origin ≠ Export"
              customcolor="yellow"
              size="small"
            />
            <StyledChip
              label="Origin ≠ Manufacturer"
              customcolor="green"
              size="small"
            />
          </>
        ) : isExporter ? (
          <StyledChip
            label="Origin ≠ Export"
            customcolor="yellow"
            size="small"
          />
        ) : isManufacturer ? (
          <StyledChip
            label="Origin ≠ Manufacturer"
            customcolor="green"
            size="small"
          />
        ) : (
          <StyledChip label="None" customcolor="red" size="small" />
        )}
      </Box>
    </Tooltip>
  );
};

interface ManufacturerData {
  manufacturer: string;
  manufacturerName?: string;
  tariffDuty: number;
}

interface TopManufacturer {
  manufacturer: string;
  manufacturerName: string;
  savings: number;
}

export const getTopManufacturers = (allVendorsData: any): TopManufacturer[] => {
  if (!allVendorsData?.data?.tradeData?.length) return [];

  const manufacturerMap = allVendorsData?.data?.tradeData?.reduce(
    (acc: Record<string, { id: string; name: string; tariffDuty: number }>, item: ManufacturerData) => {
      // Use manufacturerName if available, otherwise fall back to manufacturer ID
      const key = item.manufacturerName || item.manufacturer;
      if (!acc[key]) {
        acc[key] = { 
          id: item.manufacturer, 
          name: item.manufacturerName || item.manufacturer,
          tariffDuty: 0 
        };
      }
      acc[key].tariffDuty += item.tariffDuty;
      return acc;
    },
    {} as Record<string, { id: string; name: string; tariffDuty: number }>
  );

  const sortedManufacturers = Object.values(manufacturerMap) as Array<{ id: string; name: string; tariffDuty: number }>;
  
  return sortedManufacturers
    .sort((a, b) => b.tariffDuty - a.tariffDuty)
    .slice(0, 3)
    .map(({ id, name, tariffDuty }) => ({ 
      manufacturer: id,
      manufacturerName: name,
      savings: tariffDuty 
    }));
};

export const getStatusStep = (status: string | undefined): number => {
  switch (status) {
    case "Not contacted":
      return 1;
    case "Contacted":
      return 2;
    case "Vendor response received":
      return 3;
    case "Validate and report drafting":
      return 4;
    case "Engage customs broker":
      return 5;
    default:
      return 0; // Not eligible or undefined
  }
};
